import { useEffect, useRef } from "react";
// import { useAuth } from "../context/AuthContext";
import { LoggerService } from "../services/LoggerService";

/**
 * Hook personalizado para manejar logout automático
 * Se ejecuta cuando:
 * - Se cierra la pestaña/ventana del navegador
 * - Se pierde la conexión a internet
 * - Se detecta inactividad prolongada
 * 
 * @param {Object} options - Opciones de configuración
 * @param {number} options.inactivityTimeout - Tiempo de inactividad en ms (default: 30 minutos)
 * @param {boolean} options.enableInactivityLogout - Habilitar logout por inactividad
 * @param {boolean} options.enablePageUnloadLogout - Habilitar logout al cerrar página
 * @param {boolean} options.enableOfflineLogout - Habilitar logout al perder conexión
 */
export const useAutoLogout = ({
    currentUser,
    logout,
    inactivityTimeout = 30 * 60 * 1000, // 30 minutos
    enableInactivityLogout = true,
    enablePageUnloadLogout = true,
    enableOfflineLogout = true
} = {}) => {
    const inactivityTimerRef = useRef(null);
    const logger = LoggerService.getInstance();

    // Función para resetear el timer de inactividad
    const resetInactivityTimer = () => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
        }

        if (enableInactivityLogout && currentUser) {
            inactivityTimerRef.current = setTimeout(async () => {
                try {
                    logger.info("Auto logout por inactividad", {
                        userId: currentUser.uid,
                        email: currentUser.email,
                        category: "auth",
                        subcategory: "auto_logout"
                    });

                    await logout();
                    
                    // Mostrar mensaje al usuario
                    if (typeof window !== "undefined") {
                        alert("Sesión cerrada por inactividad. Por favor, inicie sesión nuevamente.");
                    }
                } catch (error) {
                    logger.error("Error en auto logout por inactividad", {
                        userId: currentUser?.uid,
                        error: error.message,
                        category: "auth",
                        subcategory: "auto_logout_error"
                    });
                }
            }, inactivityTimeout);
        }
    };

    // Función para manejar eventos de actividad del usuario
    const handleUserActivity = () => {
        resetInactivityTimer();
    };

    // Función para manejar el cierre de la página
    const handlePageUnload = async (_event) => {
        if (enablePageUnloadLogout && currentUser) {
            try {
                // Log del evento de cierre
                logger.info("Página cerrada - logout automático", {
                    userId: currentUser.uid,
                    email: currentUser.email,
                    category: "auth",
                    subcategory: "page_unload_logout"
                });

                // Ejecutar logout
                await logout();
            } catch (error) {
                logger.error("Error en logout por cierre de página", {
                    userId: currentUser?.uid,
                    error: error.message,
                    category: "auth",
                    subcategory: "page_unload_logout_error"
                });
            }
        }
    };

    // Función para manejar cambios en la conectividad
    const handleOnlineStatusChange = async () => {
        if (enableOfflineLogout && currentUser) {
            if (!navigator.onLine) {
                try {
                    logger.info("Conexión perdida - logout automático", {
                        userId: currentUser.uid,
                        email: currentUser.email,
                        category: "auth",
                        subcategory: "offline_logout"
                    });

                    await logout();
                    
                    if (typeof window !== "undefined") {
                        alert("Sesión cerrada por pérdida de conexión. Por favor, inicie sesión nuevamente.");
                    }
                } catch (error) {
                    logger.error("Error en logout por pérdida de conexión", {
                        userId: currentUser?.uid,
                        error: error.message,
                        category: "auth",
                        subcategory: "offline_logout_error"
                    });
                }
            }
        }
    };

    // Configurar event listeners
    useEffect(() => {
        if (!currentUser) {
            // Limpiar timer si no hay usuario
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }
            return;
        }

        // Eventos para detectar actividad del usuario
        const activityEvents = [
            "mousedown",
            "mousemove",
            "keypress",
            "scroll",
            "touchstart",
            "click"
        ];

        // Agregar event listeners para actividad
        activityEvents.forEach(event => {
            document.addEventListener(event, handleUserActivity, true);
        });

        // Event listener para cierre de página
        if (enablePageUnloadLogout) {
            window.addEventListener("beforeunload", handlePageUnload);
            window.addEventListener("unload", handlePageUnload);
        }

        // Event listeners para conectividad
        if (enableOfflineLogout) {
            window.addEventListener("online", handleOnlineStatusChange);
            window.addEventListener("offline", handleOnlineStatusChange);
        }

        // Iniciar timer de inactividad
        resetInactivityTimer();

        // Cleanup function
        return () => {
            // Remover event listeners de actividad
            activityEvents.forEach(event => {
                document.removeEventListener(event, handleUserActivity, true);
            });

            // Remover event listeners de página
            if (enablePageUnloadLogout) {
                window.removeEventListener("beforeunload", handlePageUnload);
                window.removeEventListener("unload", handlePageUnload);
            }

            // Remover event listeners de conectividad
            if (enableOfflineLogout) {
                window.removeEventListener("online", handleOnlineStatusChange);
                window.removeEventListener("offline", handleOnlineStatusChange);
            }

            // Limpiar timer
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, inactivityTimeout, enableInactivityLogout, enablePageUnloadLogout, enableOfflineLogout]);

    // Retornar función para resetear manualmente el timer
    return {
        resetTimer: resetInactivityTimer
    };
}; 